import React, { useState } from 'react'
import styled from 'styled-components'
import { PieChart } from 'react-minimal-pie-chart'
import HomeWorkIcon from '@mui/icons-material/HomeWorkOutlined'
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartmentOutlined'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import ExpandLessIcon from '@mui/icons-material/ExpandLess'
import GavelIcon from '@mui/icons-material/Gavel'
import HistoryIcon from '@mui/icons-material/HistoryOutlined'
import ShieldIcon from '@mui/icons-material/ShieldOutlined'
import InfoIcon from '@mui/icons-material/InfoOutlined'
import EventNoteIcon from '@mui/icons-material/EventNoteOutlined'
import PeopleIcon from '@mui/icons-material/PeopleOutline'
import ApartmentIcon from '@mui/icons-material/ApartmentOutlined'
import RuleFolderIcon from '@mui/icons-material/RuleFolderOutlined'
import AssessmentIcon from '@mui/icons-material/AssessmentOutlined'
import InsertChartIcon from '@mui/icons-material/InsertChartOutlined'
import DescriptionIcon from '@mui/icons-material/DescriptionOutlined'
import FactCheckIcon from '@mui/icons-material/FactCheckOutlined'
import BuildIcon from '@mui/icons-material/BuildOutlined'
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheckOutlined'
import AssignmentIcon from '@mui/icons-material/AssignmentOutlined'
import ReportProblemIcon from '@mui/icons-material/ReportProblemOutlined'
import StickyNote2Icon from '@mui/icons-material/StickyNote2Outlined'
import EmailIcon from '@mui/icons-material/EmailOutlined'
import TimelineIcon from '@mui/icons-material/TimelineOutlined'
import SiteRisksIcon from '@mui/icons-material/GppMaybeOutlined'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import WikiClaim from './WikiClaim'
import WikiActionChip from './WikiActionChip'

const SECTION_ICONS = {
  overview: InfoIcon,
  legislation: GavelIcon,
  activity: HistoryIcon,
  risk_compliance: ShieldIcon,
  building_info: HomeWorkIcon,
  planning_constraints: SiteRisksIcon,
  forecast: EventNoteIcon,
  people: PeopleIcon,
  units: ApartmentIcon,
  compliance: RuleFolderIcon,
  compliance_sets: FactCheckIcon,
  analytics_templates: InsertChartIcon,
  checklist_templates: InsertChartIcon,
  reports: AssessmentIcon,
  bp_audits: FactCheckIcon,
  building_assets: BuildIcon,
  documents: DescriptionIcon,
  action_items: PlaylistAddCheckIcon,
  tasks: AssignmentIcon,
  incidents: ReportProblemIcon,
  notes: StickyNote2Icon,
  emails: EmailIcon,
  recent_events: TimelineIcon,
}
import {
  actionsForComplianceRow,
  actionsForDocumentRow,
  actionsForPerson,
  sectionHeaderActions,
} from '../actions'

const Section = styled.section`
  margin-bottom: 32px;
  &:last-child { margin-bottom: 0; }
`

const SectionHead = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-bottom: 14px;
  border-bottom: 4px solid #33becc;
  padding-bottom: 8px;
  gap: 12px;
  flex-wrap: wrap;
`

const SectionTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #0c1229;
`

const SectionActions = styled.div`
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  align-items: center;
`

const RowActions = styled.div`
  display: flex;
  gap: 4px;
  flex-wrap: wrap;
  align-items: center;
  justify-content: flex-end;
`

const Paragraph = styled.p`
  font-size: 14px;
  line-height: 1.65;
  color: #333;
  margin: 10px 0;
`

const FactGrid = styled.div`
  display: grid;
  grid-template-columns: 160px 1fr;
  row-gap: 8px;
  column-gap: 16px;
  font-size: 13px;
  margin: 12px 0 4px;
  padding: 14px 16px;
  background: #fcfdfe;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
`

const FactKey = styled.div`
  color: #999;
  font-size: 12px;
`

const FactValue = styled.div`
  color: #333;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  margin: 12px 0;

  th {
    text-align: left;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.11px;
    color: #999;
    text-transform: uppercase;
    padding: 8px 10px;
    border-bottom: 1px solid #f2f2f2;
  }
  td {
    padding: 10px;
    border-bottom: 1px solid #f2f2f2;
    color: #333;
    vertical-align: top;
  }
  tr:last-child td {
    border-bottom: none;
  }
`

const StatusChip = styled.span`
  display: inline-block;
  padding: 3px 9px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 500;
  ${({ tone }) => {
    if (tone === 'overdue') return 'background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;'
    if (tone === 'expiring_soon') return 'background:#fff7ed;color:#c2410c;border:1px solid #fed7aa;'
    if (tone === 'missing') return 'background:#fef2f2;color:#b91c1c;border:1px solid #fecaca;'
    if (tone === 'current') return 'background:#ecfdf5;color:#065f46;border:1px solid #a7f3d0;'
    return 'background:#f2f2f2;color:#333;'
  }}
`

const People = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 10px;
  margin: 12px 0;
`

const Person = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px 12px;
  background: #fcfdfe;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  align-items: center;
  justify-content: space-between;
`

const PersonWho = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  min-width: 0;
`

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  background: #33becc;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`

const PersonName = styled.div`
  font-size: 13px;
  font-weight: 500;
`

const PersonRole = styled.div`
  font-size: 11px;
  color: #999;
  letter-spacing: 0.2px;
`

const Timeline = styled.div`
  margin: 12px 0;
`

const TimelineItem = styled.div`
  display: flex;
  gap: 12px;
  padding: 10px 0;
  border-bottom: 1px solid #f2f2f2;
  &:last-child { border-bottom: none; }
`

const TimelineDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  background: #33becc;
  margin-top: 6px;
  flex-shrink: 0;
`

const TimelineWhen = styled.div`
  font-size: 11px;
  color: #999;
  letter-spacing: 0.2px;
  min-width: 90px;
`

const TimelineDesc = styled.div`
  font-size: 13px;
  color: #333;
  line-height: 1.5;
`

const initials = (name) =>
  (name || '?')
    .split(' ')
    .filter(Boolean)
    .map((p) => p[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()

const formatRelation = (rel) =>
  (rel || '')
    .toString()
    .toLowerCase()
    .replace(/_/g, ' ')
    .replace(/\b\w/g, (c) => c.toUpperCase())

const formatDate = (iso) => {
  if (!iso) return '—'
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })
  } catch {
    return iso
  }
}

const stateLabel = (state, days) => {
  if (state === 'overdue') return `Overdue ${Math.abs(days || 0)}d`
  if (state === 'expiring_soon') return `Due in ${days}d`
  if (state === 'missing') return 'Missing'
  if (state === 'current') return 'Current'
  return state
}

const formatBuildingTypes = (types) => {
  if (!Array.isArray(types) || types.length === 0) return '—'
  return types
    .map((t) => {
      if (typeof t === 'string') return t
      if (t && typeof t === 'object') {
        const name = t.type || t.name || t.label || ''
        const units = t.units ? ` (${t.units} units)` : ''
        return `${name}${units}`.trim()
      }
      return String(t)
    })
    .filter(Boolean)
    .map((s) => s.charAt(0).toUpperCase() + s.slice(1))
    .join(', ')
}

const SeeAllLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 6px 12px;
  margin-top: 10px;
  border-radius: 9999px;
  font-size: 12px;
  font-weight: 500;
  background: #fff;
  color: #d24724;
  border: 1px solid #fbede9;
  text-decoration: none;
  cursor: pointer;

  &:hover {
    background: #fbede9;
  }
`

const BulletList = styled.ul`
  margin: 10px 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;

  li {
    position: relative;
    padding-left: 20px;
    line-height: 1.55;
    font-size: 14px;
    color: #333;
  }
  li::before {
    content: '';
    position: absolute;
    left: 4px;
    top: 10px;
    width: 6px;
    height: 6px;
    border-radius: 9999px;
    background: #33becc;
  }
`

const EmptyCopy = styled.div`
  font-size: 13px;
  color: #999;
  padding: 14px 16px;
  background: #fcfdfe;
  border: 1px dashed #f2f2f2;
  border-radius: 8px;
  margin: 12px 0;
`

const ComplianceBar = styled.div`
  margin: 12px 0;
  background: #fcfdfe;
  border: 1px solid #f2f2f2;
  border-radius: 8px;
  padding: 14px 16px;
`

const ComplianceBarHeader = styled.div`
  display: flex;
  justify-content: space-between;
  font-size: 12px;
  color: #666;
  margin-bottom: 8px;
  text-transform: uppercase;
  letter-spacing: 0.2px;
  font-weight: 500;
`

const ComplianceTrack = styled.div`
  display: flex;
  height: 10px;
  border-radius: 9999px;
  overflow: hidden;
  background: #f2f2f2;
`

const ComplianceSegment = styled.div`
  height: 100%;
  background: ${({ tone }) => (
    tone === 'current' ? '#10b981'
    : tone === 'expiring_soon' ? '#f59e0b'
    : tone === 'overdue' ? '#dc2626'
    : tone === 'missing' ? '#7c1d1d'
    : '#cbd5e1'
  )};
`

const ComplianceLegend = styled.div`
  display: flex;
  gap: 14px;
  margin-top: 8px;
  font-size: 11px;
  flex-wrap: wrap;
`

const LegendDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  margin-right: 5px;
  background: ${({ tone }) => (
    tone === 'current' ? '#10b981'
    : tone === 'expiring_soon' ? '#f59e0b'
    : tone === 'overdue' ? '#dc2626'
    : tone === 'missing' ? '#7c1d1d'
    : '#cbd5e1'
  )};
`

const ComplianceVisual = styled.div`
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 24px;
  align-items: center;
  background: linear-gradient(135deg, #fcfdfe 0%, #fff 100%);
  border: 1px solid #f2f2f2;
  border-radius: 12px;
  padding: 18px 22px;
  margin: 12px 0;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`

const DonutWrap = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
`

const DonutCenter = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  pointer-events: none;
`

const DonutBigNumber = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ tone }) => (
    tone === 'crit' ? '#b91c1c'
    : tone === 'ok' ? '#065f46'
    : '#0c1229'
  )};
  line-height: 1;
`

const DonutCaption = styled.div`
  font-size: 11px;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  margin-top: 4px;
`

const ComplianceLegendGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 18px;
`

const LegendRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 13px;
  color: #333;
`

const LegendRowLabel = styled.span`
  display: flex;
  align-items: center;
  gap: 6px;
  text-transform: capitalize;
`

const LegendRowValue = styled.span`
  font-weight: 600;
  color: ${({ tone }) => (
    tone === 'overdue' ? '#b91c1c'
    : tone === 'missing' ? '#7c1d1d'
    : tone === 'expiring_soon' ? '#c2410c'
    : tone === 'current' ? '#065f46'
    : '#333'
  )};
`

const PrioritiesStrip = styled.div`
  background: linear-gradient(135deg, #fff7ed 0%, #fef2f2 100%);
  border: 1px solid #fed7aa;
  border-radius: 12px;
  padding: 14px 18px 6px;
  margin: 12px 0 16px;
`

const PrioritiesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9a3412;

  svg {
    font-size: 16px !important;
    width: 16px;
    height: 16px;
    color: #c2410c;
  }
`

const PrioritiesSub = styled.div`
  font-size: 12px;
  color: #9a3412;
  margin-bottom: 10px;
  line-height: 1.4;
`

const PriorityRow = styled.div`
  display: grid;
  grid-template-columns: 12px minmax(0, 1fr) auto auto;
  align-items: center;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #fed7aa;
  font-size: 13px;
`

const SeverityDot = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 9999px;
  display: inline-block;
  background: ${({ severity }) => (
    severity === 'critical' ? '#b91c1c'
    : severity === 'high' ? '#dc2626'
    : severity === 'medium' ? '#f59e0b'
    : severity === 'low' ? '#84cc16'
    : '#9ca3af'
  )};
`

const PriorityName = styled.div`
  font-weight: 500;
  color: #0c1229;
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;

  > span:first-of-type {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

const SafetyBadge = styled.span`
  font-size: 9px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #b91c1c;
  background: #fee2e2;
  padding: 2px 6px;
  border-radius: 4px;
  white-space: nowrap;
`

const PriorityWhen = styled.div`
  font-size: 11px;
  color: #78716c;
  text-align: right;
  white-space: nowrap;
`

const HiddenToggle = styled.button`
  background: none;
  border: none;
  color: #6b7280;
  font-size: 11px;
  font-weight: 500;
  cursor: pointer;
  padding: 4px 0 0;
  display: inline-flex;
  align-items: center;
  gap: 4px;

  svg {
    font-size: 14px !important;
    width: 14px;
    height: 14px;
  }

  &:hover {
    color: #0c1229;
  }
`

const HiddenPanel = styled.div`
  margin-top: 8px;
  padding: 12px 14px;
  background: #fafafa;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
`

const HiddenSubhead = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.4px;
  color: #9ca3af;
  font-weight: 600;
  margin-bottom: 6px;
  &:not(:first-child) { margin-top: 12px; }
`

const HiddenChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`

const HiddenChip = styled.span`
  font-size: 11px;
  background: #fff;
  color: #4b5563;
  padding: 4px 9px;
  border-radius: 6px;
  border: 1px solid #e5e7eb;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`

const HiddenChipReason = styled.span`
  color: #9ca3af;
  font-size: 10px;
`

const DataQualityNote = styled.div`
  margin-top: 10px;
  padding: 10px 12px;
  background: #fefce8;
  border: 1px solid #fde68a;
  border-radius: 8px;
  font-size: 12px;
  color: #713f12;
  line-height: 1.5;
`

const FriendlyEmpty = styled.div`
  background: linear-gradient(135deg, #fcfdfe 0%, #fff 100%);
  border: 1px dashed #e5e7eb;
  border-radius: 12px;
  padding: 28px 20px;
  margin: 14px 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 8px;

  svg {
    font-size: 40px !important;
    width: 40px;
    height: 40px;
    color: #cbd5e1;
  }
`

const FriendlyEmptyText = styled.div`
  font-size: 13px;
  color: #6b7280;
  max-width: 380px;
  line-height: 1.5;
`

const PlanningSummaryRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 6px;
  margin-bottom: 12px;
`

const PlanningMeta = styled.span`
  font-size: 11px;
  color: #999;
  margin-left: 4px;
`

const PlanningList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
`

const PlanningItem = styled.div`
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid ${({ severity }) => (severity === 'critical' ? '#fecaca' : severity === 'warning' ? '#fed7aa' : '#bfdbfe')};
  background: ${({ severity }) => (severity === 'critical' ? '#fef2f2' : severity === 'warning' ? '#fff7ed' : '#eff6ff')};
`

const PlanningItemHead = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
`

const PlanningItemLabel = styled.span`
  font-weight: 600;
  color: #0c1229;
`

const PlanningItemGroup = styled.span`
  font-size: 11px;
  color: #999;
  margin-left: auto;
  white-space: nowrap;
`

const PlanningItemEntities = styled.div`
  font-size: 12px;
  color: #333;
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 2px;

  a { color: inherit; }
`

const PlanningItemDesc = styled.div`
  font-size: 12px;
  line-height: 1.45;
  color: #666;
  margin-top: 6px;
`

const PlanningClear = styled.div`
  font-size: 12px;
  line-height: 1.5;
  color: #6b7280;

  strong { color: #065f46; font-weight: 600; }
`

const GenerateBtn = styled.button`
  margin-top: 6px;
  height: 40px;
  padding: 0 18px;
  border: none;
  border-radius: 8px;
  background: #d24724;
  color: #fff;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.15s;

  &:hover:not(:disabled) { background: #b13a1d; }
  &:disabled { opacity: 0.6; cursor: default; }
`

const NARRATIVE_EMPTY_COPY = {
  overview: "Guru hasn't written the overview yet. Refresh the wiki to try again.",
  legislation: "Guru hasn't produced the legislation summary yet. Refresh the wiki to try again.",
  activity: 'No recent activity to summarise on this Building Passport.',
  risk_compliance: 'No risk or compliance summary has been produced yet. Refresh the wiki to try again.',
}

const NARRATIVE_KEYS = new Set(['overview', 'legislation', 'activity', 'risk_compliance'])
const ALWAYS_SHOW_SECTIONS = new Set([
  'overview', 'legislation', 'activity', 'risk_compliance',
  'building_info', 'people', 'compliance', 'documents',
])

const sectionIsEmpty = (section) => {
  if (section.kind === 'narrative') return (section.claims || []).length === 0
  const d = section.data || {}
  if (Array.isArray(d.rows)) return d.rows.length === 0
  if (Array.isArray(d.events)) return d.events.length === 0
  return false
}

const WikiSection = ({ section, onCitationActivate, bpId, hasTaskManagement = false, onRegenerate, regenerating = false }) => {
  const headerActions = bpId ? sectionHeaderActions(section.key, bpId, hasTaskManagement) : []
  const [excludedOpen, setExcludedOpen] = useState(false)
  if (sectionIsEmpty(section) && !ALWAYS_SHOW_SECTIONS.has(section.key)) return null

  const renderBody = () => {
    if (section.kind === 'narrative') {
      const claims = section.claims || []
      if (claims.length === 0) {
        const copy = NARRATIVE_EMPTY_COPY[section.key]
          || (NARRATIVE_KEYS.has(section.key) ? 'No content available yet.' : null)
        if (copy === null) return null
        const SectionIcon = SECTION_ICONS[section.key] || InfoIcon
        return (
          <FriendlyEmpty>
            <SectionIcon />
            <FriendlyEmptyText>{copy}</FriendlyEmptyText>
            {onRegenerate && section.key !== 'activity' && (
              <GenerateBtn type="button" onClick={onRegenerate} disabled={regenerating}>
                {regenerating ? 'Generating…' : 'Generate now'}
              </GenerateBtn>
            )}
          </FriendlyEmpty>
        )
      }
      if (section.key === 'legislation') {
        return (
          <BulletList>
            {claims.map((claim, idx) => (
              <li key={idx}>
                <WikiClaim claim={claim} onCitationActivate={onCitationActivate} />
              </li>
            ))}
          </BulletList>
        )
      }
      return (
        <Paragraph>
          {claims.map((claim, idx) => (
            <React.Fragment key={idx}>
              <WikiClaim claim={claim} onCitationActivate={onCitationActivate} />{' '}
            </React.Fragment>
          ))}
        </Paragraph>
      )
    }

    if (section.kind === 'structured' && section.key === 'building_info') {
      const d = section.data || {}
      const epc = d.epc || {}
      const epcLabel = epc.onFile
        ? (epc.isOverdue
          ? `On file · expired ${epc.expiresAt ? formatDate(epc.expiresAt) : 'unknown date'}`
          : `On file${epc.expiresAt ? ` · valid until ${formatDate(epc.expiresAt)}` : ''}`)
        : 'Not on file'
      const subEndsAt = d.subscription?.endsAt
      let subLabel = 'Not tracked'
      if (subEndsAt) {
        const endsDate = new Date(subEndsAt)
        const today = new Date()
        if (endsDate < today) {
          subLabel = `Expired ${formatDate(subEndsAt)}`
        } else {
          subLabel = `Active · renews ${formatDate(subEndsAt)}`
        }
      }
      const rows = [
        ['UPRN', d.uprn || '—'],
        ['Building types', formatBuildingTypes(d.buildingTypes)],
        ['Floors', `${d.floorsAbove ?? 0} above · ${d.floorsBelow ?? 0} below`],
        ['Occupants (approx)', `${d.occupants ?? '—'} · ${d.elderlyOccupants ?? 0} elderly · ${d.disabledOccupants ?? 0} disabled`],
        ['Occupied', d.occupied ? 'Yes' : 'No'],
        ['Cladding issue', d.claddingIssue ? 'Yes' : 'No'],
        ['Flood risk', d.floodRisk ? 'Yes' : 'No'],
        ['EPC rating', epcLabel],
        ['Subscription', subLabel],
        ['Inbox email', d.inboxEmail || '—'],
      ]
      return (
        <FactGrid>
          {rows.map(([k, v]) => (
            <React.Fragment key={k}>
              <FactKey>{k}</FactKey>
              <FactValue>{v}</FactValue>
            </React.Fragment>
          ))}
        </FactGrid>
      )
    }

    if (section.kind === 'structured' && section.key === 'planning_constraints') {
      const d = section.data || {}
      const dashboardHref = bpId ? `/bp-list/${bpId}/planning` : null
      if (!d.status || (d.status !== 'synced' && !(d.summary?.checked > 0))) {
        const copy = d.status === 'not_found'
          ? 'planning.data.gov.uk could not place this building (England only, and the UPRN must be in its index).'
          : d.status && d.status !== 'failed'
            ? 'A planning data screening is in progress. This section fills in once it completes.'
            : (d.emptyCopy || 'This building has not been screened against planning.data.gov.uk yet.')
        return (
          <FriendlyEmpty>
            <SiteRisksIcon />
            <FriendlyEmptyText>{copy}</FriendlyEmptyText>
            {dashboardHref && <SeeAllLink href={dashboardHref}>Open site risks</SeeAllLink>}
          </FriendlyEmpty>
        )
      }
      const summary = d.summary || {}
      const flagged = d.flagged || []
      const loc = d.location || {}
      const locParts = [loc.localPlanningAuthority, loc.ward && `${loc.ward} ward`].filter(Boolean)
      return (
        <div>
          <PlanningSummaryRow>
            <StatusChip tone={summary.critical > 0 ? 'overdue' : summary.flagged > 0 ? 'expiring_soon' : 'current'}>
              {summary.flagged > 0 ? `${summary.flagged} flagged` : 'Nothing flagged'}
            </StatusChip>
            {summary.critical > 0 && <StatusChip tone='overdue'>{summary.critical} critical</StatusChip>}
            {summary.warning > 0 && <StatusChip tone='expiring_soon'>{summary.warning} warning</StatusChip>}
            <StatusChip tone='current'>{summary.clear || 0} clear</StatusChip>
            <StatusChip>{summary.noneRecorded || 0} none recorded</StatusChip>
            <PlanningMeta>
              {summary.checked} checks
              {locParts.length > 0 && ` · ${locParts.join(', ')}`}
              {d.lastCheckedAt && ` · screened ${formatDate(d.lastCheckedAt)}`}
            </PlanningMeta>
          </PlanningSummaryRow>
          {flagged.length > 0 && (
            <PlanningList>
              {flagged.map((item) => (
                <PlanningItem key={item.dataset} severity={item.severity}>
                  <PlanningItemHead>
                    <SeverityDot severity={item.severity === 'warning' ? 'medium' : item.severity === 'info' ? 'low' : item.severity} />
                    <PlanningItemLabel>{item.label}</PlanningItemLabel>
                    <PlanningItemGroup>{item.group}</PlanningItemGroup>
                  </PlanningItemHead>
                  <PlanningItemEntities>
                    {(item.entities || []).map((e, idx) => (
                      <span key={`${item.dataset}:${idx}`}>
                        {e.name || item.label}
                        {(e.properties || [])
                          .filter((p) => ['flood-risk-level', 'listed-building-grade', 'park-and-garden-grade'].includes(p.key))
                          .map((p) => ` · ${p.label} ${p.value}`)
                          .join('')}
                        {(e.entityUrl || e.documentationUrl) && (
                          <a href={e.entityUrl || e.documentationUrl} target='_blank' rel='noreferrer' title={e.entityUrl ? 'View on planning.data.gov.uk' : 'Official record'}>
                            <OpenInNewIcon style={{ fontSize: 12, width: 12, height: 12, verticalAlign: 'middle', marginLeft: 3 }} />
                          </a>
                        )}
                      </span>
                    ))}
                  </PlanningItemEntities>
                  <PlanningItemDesc>{item.description}</PlanningItemDesc>
                </PlanningItem>
              ))}
            </PlanningList>
          )}
          {(d.clear || []).length > 0 && (
            <PlanningClear>
              <strong>Clear:</strong> {d.clear.join(', ')}.
            </PlanningClear>
          )}
          {dashboardHref && <SeeAllLink href={dashboardHref}>Open site risks dashboard</SeeAllLink>}
        </div>
      )
    }

    if (section.kind === 'people') {
      const rows = section.data?.rows || []
      if (rows.length === 0) {
        return (
          <FriendlyEmpty>
            <PeopleIcon />
            <FriendlyEmptyText>{section.data?.emptyCopy || 'No members assigned to this Building Passport yet.'}</FriendlyEmptyText>
          </FriendlyEmpty>
        )
      }
      return (
        <People>
          {rows.map((m) => (
            <Person key={`${m.userId}:${m.relationType}`}>
              <PersonWho>
                <Avatar>{initials(m.name)}</Avatar>
                <div>
                  <PersonName>{m.name}</PersonName>
                  <PersonRole>
                    {formatRelation(m.relationType)}
                    {m.assignedAt && ` · since ${formatDate(m.assignedAt)}`}
                    {!m.active && ' · revoked'}
                  </PersonRole>
                </div>
              </PersonWho>
              {bpId && (
                <RowActions>
                  {actionsForPerson(m, bpId).map((a, idx) => (
                    <WikiActionChip key={idx} Icon={a.Icon} label={a.label} href={a.href} variant={a.variant} title={a.title} />
                  ))}
                </RowActions>
              )}
            </Person>
          ))}
        </People>
      )
    }

    if (section.kind === 'table' && section.key === 'compliance') {
      const rows = section.data?.rows || []
      const total = section.data?.total ?? rows.length
      const uniqueTypes = section.data?.uniqueTypes ?? rows.length
      const shown = section.data?.shown ?? rows.length
      const seeAllLink = section.data?.seeAllLink
      const dataQualityExcluded = section.data?.dataQualityExcluded ?? 0
      const excludedTypes = section.data?.excludedTypes || []
      const topPriorities = section.data?.topPriorities || []
      const noExpiry = section.data?.dataQualityNoExpiry || []
      const excludedByReason = excludedTypes.reduce((acc, e) => {
        const key = e.reason || 'other'
        acc[key] = acc[key] || []
        acc[key].push(e)
        return acc
      }, {})
      const excludedReasonLabels = {
        non_renewable_reference: 'Reference documents (no renewal cycle)',
        sanity_cap_junk_overdue: 'Historical records (long beyond renewal cycle)',
      }

      // Use full health counts when available (covers ALL unique types, not just
      // the preview rows). Falls back to counting the preview rows.
      const stateCounts = section.data?.healthFull || rows.reduce((acc, r) => {
        const s = r.state || 'unknown'
        acc[s] = (acc[s] || 0) + 1
        return acc
      }, {})
      const totalCounted = Object.values(stateCounts).reduce((a, b) => a + b, 0)
      const orderedStates = ['current', 'expiring_soon', 'overdue', 'missing']
      const healthPct = totalCounted > 0
        ? Math.round((100 * (stateCounts.current || 0)) / totalCounted)
        : null

      const stateColors = {
        current: '#10b981',
        expiring_soon: '#f59e0b',
        overdue: '#dc2626',
        missing: '#7c1d1d',
      }
      const donutData = orderedStates
        .map((state) => {
          const n = stateCounts[state] || 0
          return n > 0 ? { title: state.replace(/_/g, ' '), value: n, color: stateColors[state] } : null
        })
        .filter(Boolean)

      const donutTone = (stateCounts.overdue || 0) + (stateCounts.missing || 0) > 0
        ? 'crit'
        : (stateCounts.expiring_soon || 0) > 0 ? null : 'ok'

      return (
        <>
          {topPriorities.length > 0 && (
            <PrioritiesStrip>
              <PrioritiesHeader>
                <LocalFireDepartmentIcon />
                Act on these first
              </PrioritiesHeader>
              <PrioritiesSub>
                Ranked by safety-criticality and urgency. {topPriorities.length === 1 ? 'This is' : `These ${topPriorities.length} are`} the most important compliance {topPriorities.length === 1 ? 'item' : 'items'} on this Building Passport right now.
              </PrioritiesSub>
              {topPriorities.map((p) => (
                <PriorityRow key={p.fileTypeCode || p.fileTypeName}>
                  <SeverityDot severity={p.severity} title={p.severity} />
                  <PriorityName>
                    <span>{p.fileTypeName}</span>
                    {p.isSafetyCritical && <SafetyBadge>Safety-critical</SafetyBadge>}
                  </PriorityName>
                  <StatusChip tone={p.state}>{stateLabel(p.state, p.daysUntilExpiry)}</StatusChip>
                  <PriorityWhen>{formatDate(p.expirationDate)}</PriorityWhen>
                </PriorityRow>
              ))}
              {bpId && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, paddingTop: 10, marginTop: 4, borderTop: '1px solid #fed7aa' }}>
                  {actionsForComplianceRow(topPriorities[0], bpId).slice(0, 3).map((a, i) => (
                    <WikiActionChip key={i} Icon={a.Icon} label={`#1: ${a.label}`} href={a.href} variant={a.variant} title={a.title} />
                  ))}
                </div>
              )}
            </PrioritiesStrip>
          )}
          {totalCounted > 0 && (
            <ComplianceVisual>
              <DonutWrap>
                <PieChart
                  data={donutData}
                  lineWidth={20}
                  paddingAngle={donutData.length > 1 ? 2 : 0}
                  startAngle={-90}
                  animate
                  animationDuration={450}
                />
                <DonutCenter>
                  <DonutBigNumber tone={donutTone}>{healthPct ?? 0}%</DonutBigNumber>
                  <DonutCaption>Current</DonutCaption>
                </DonutCenter>
              </DonutWrap>
              <ComplianceLegendGrid>
                {orderedStates.map((state) => {
                  const n = stateCounts[state] || 0
                  if (n === 0) return null
                  return (
                    <LegendRow key={state}>
                      <LegendRowLabel><LegendDot tone={state} />{state.replace(/_/g, ' ')}</LegendRowLabel>
                      <LegendRowValue tone={state}>{n}</LegendRowValue>
                    </LegendRow>
                  )
                })}
                <LegendRow>
                  <LegendRowLabel style={{ color: '#999' }}>Total document types</LegendRowLabel>
                  <LegendRowValue>{totalCounted}</LegendRowValue>
                </LegendRow>
              </ComplianceLegendGrid>
            </ComplianceVisual>
          )}
          <Table>
            <thead>
              <tr>
                <th>Document type</th>
                <th>Status</th>
                <th>Expires</th>
                <th style={{ textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r, idx) => (
                <tr key={`${r.fileTypeCode}-${idx}`}>
                  <td>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                      {r.fileTypeName || r.fileTypeCode}
                      {r.isSafetyCritical && <SafetyBadge>Safety-critical</SafetyBadge>}
                    </span>
                  </td>
                  <td>
                    <StatusChip tone={r.state}>{stateLabel(r.state, r.daysUntilExpiry)}</StatusChip>
                  </td>
                  <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.expirationDate)}</td>
                  <td>
                    {bpId && (
                      <RowActions>
                        {actionsForComplianceRow(r, bpId).map((a, i) => (
                          <WikiActionChip key={i} Icon={a.Icon} label={a.label} href={a.href} variant={a.variant} title={a.title} />
                        ))}
                      </RowActions>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
            {shown < uniqueTypes
              ? `Showing ${shown} of ${uniqueTypes.toLocaleString()} renewable document type${uniqueTypes === 1 ? '' : 's'} · rolled up from ${total.toLocaleString()} file${total === 1 ? '' : 's'}, safety-critical and problem types surfaced first.`
              : `Showing ${uniqueTypes.toLocaleString()} renewable document type${uniqueTypes === 1 ? '' : 's'} (rolled up from ${total.toLocaleString()} file${total === 1 ? '' : 's'}).`}
          </div>
          {(dataQualityExcluded > 0 || excludedTypes.length > 0) && (
            <>
              <HiddenToggle onClick={() => setExcludedOpen((o) => !o)}>
                {excludedOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                {excludedOpen ? 'Hide' : 'Show'} {dataQualityExcluded.toLocaleString()} file type{dataQualityExcluded === 1 ? '' : 's'} filtered as reference or historical
              </HiddenToggle>
              {excludedOpen && (
                <HiddenPanel>
                  {Object.entries(excludedByReason).map(([reason, items]) => (
                    <React.Fragment key={reason}>
                      <HiddenSubhead>
                        {excludedReasonLabels[reason] || reason} · {items.length}
                      </HiddenSubhead>
                      <HiddenChipList>
                        {items.map((e) => (
                          <HiddenChip key={e.fileTypeCode}>
                            {e.fileTypeName}
                            {e.daysOverdue ? (
                              <HiddenChipReason>· {e.daysOverdue.toLocaleString()}d stamped expiry</HiddenChipReason>
                            ) : null}
                          </HiddenChip>
                        ))}
                      </HiddenChipList>
                    </React.Fragment>
                  ))}
                  {excludedTypes.length < dataQualityExcluded && (
                    <div style={{ fontSize: 11, color: '#9ca3af', marginTop: 10 }}>
                      Showing first {excludedTypes.length} of {dataQualityExcluded.toLocaleString()} filtered types.
                    </div>
                  )}
                </HiddenPanel>
              )}
            </>
          )}
          {noExpiry.length > 0 && (
            <DataQualityNote>
              <strong>{noExpiry.length} renewable file type{noExpiry.length === 1 ? '' : 's'}</strong> {noExpiry.length === 1 ? 'is' : 'are'} uploaded but {noExpiry.length === 1 ? 'has' : 'have'} no expiry date set, so {noExpiry.length === 1 ? "it isn't" : "they aren't"} tracked on the renewal calendar:&nbsp;
              <span style={{ color: '#92400e' }}>
                {noExpiry.slice(0, 6).map((n) => n.fileTypeName).join(', ')}
                {noExpiry.length > 6 && ` (+${noExpiry.length - 6} more)`}.
              </span>
            </DataQualityNote>
          )}
          {seeAllLink && (
            <SeeAllLink href={seeAllLink}>Open the renewal calendar →</SeeAllLink>
          )}
        </>
      )
    }

    if (section.kind === 'table' && section.key === 'documents') {
      const rows = section.data?.rows || []
      const seeAllLink = section.data?.seeAllLink
      const docStats = section.data?.docStats || {}
      const topCategories = section.data?.topCategories || []
      const uniqueTypesTotal = section.data?.uniqueTypesTotal ?? topCategories.length
      const maxCatCount = topCategories.reduce((m, c) => Math.max(m, c.count || 0), 0) || 1
      return (
        <>
          {docStats.total > 0 && (
            <FactGrid>
              <FactKey>Documents on file</FactKey>
              <FactValue>
                <strong>{(docStats.current ?? 0).toLocaleString()}</strong> current
                <span style={{ color: '#999' }}>
                  {' · '}{(docStats.superseded ?? 0).toLocaleString()} older versions superseded by newer uploads
                  {' · '}{(docStats.total ?? 0).toLocaleString()} files in total
                </span>
              </FactValue>
            </FactGrid>
          )}

          {topCategories.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.2px', fontWeight: 600, margin: '14px 0 6px' }}>
                Top {topCategories.length} categories · {uniqueTypesTotal} unique types in total
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {topCategories.map((c) => (
                  <div key={c.code} style={{ display: 'grid', gridTemplateColumns: '1fr 120px 50px', alignItems: 'center', gap: 12, fontSize: 13 }}>
                    <span style={{ color: '#333' }}>{c.name}</span>
                    <div style={{ height: 6, background: '#f2f2f2', borderRadius: 9999, overflow: 'hidden' }}>
                      <div style={{ width: `${Math.round((c.count / maxCatCount) * 100)}%`, height: '100%', background: '#33becc' }} />
                    </div>
                    <span style={{ color: '#666', fontWeight: 500, textAlign: 'right' }}>{c.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {rows.length > 0 && (
            <>
              <div style={{ fontSize: 11, color: '#666', textTransform: 'uppercase', letterSpacing: '0.2px', fontWeight: 600, margin: '18px 0 6px' }}>
                Most recent uploads
              </div>
              <Table>
                <thead>
                  <tr>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Uploaded</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.fileId}>
                      <td>{r.filename}</td>
                      <td style={{ color: '#999', fontSize: 12 }}>{r.fileTypeName || r.fileTypeCode || '—'}</td>
                      <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.uploadedAt)}</td>
                      <td>
                        {bpId && (
                          <RowActions>
                            {actionsForDocumentRow(r, bpId).map((a, i) => (
                              <WikiActionChip key={i} Icon={a.Icon} label={a.label} href={a.href} variant={a.variant} title={a.title} />
                            ))}
                          </RowActions>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </>
          )}

          {seeAllLink && (
            <SeeAllLink href={seeAllLink}>Open all documents →</SeeAllLink>
          )}
        </>
      )
    }

    if (section.kind === 'table' && ['tasks', 'action_items', 'incidents', 'faults', 'units', 'notes', 'emails', 'forecast', 'reports', 'analytics_templates', 'checklist_templates', 'compliance_sets', 'bp_audits', 'building_assets'].includes(section.key)) {
      const rows = section.data?.rows || []
      const total = section.data?.total ?? rows.length
      const seeAllLink = section.data?.seeAllLink

      let unitsSummary = null
      let hideUnitSampleTable = false
      if (section.key === 'units' && rows.length > 0) {
        const byType = section.data?.byType || {}
        const occupied = section.data?.occupiedCount ?? 0
        const vacant = section.data?.vacantCount ?? 0
        const totalUnits = section.data?.total ?? rows.length
        const occupants = section.data?.occupantsTotal ?? 0
        const floorRange = section.data?.floorRange
        const breakdown = Object.entries(byType)
          .map(([t, n]) => `${n} ${t.replace(/_/g, ' ')}`)
          .join(' · ')
        const hasMeaningfulFloors = floorRange && !(floorRange.min === 0 && floorRange.max === 0)
        const hasMeaningfulNames = rows.some(
          (r) => r.name && !/^Unnamed unit #\d+$/i.test(r.name) && r.name !== 'Apartment',
        )
        hideUnitSampleTable = !hasMeaningfulNames
        unitsSummary = (
          <FactGrid>
            {breakdown && <><FactKey>Mix</FactKey><FactValue>{breakdown}</FactValue></>}
            <FactKey>Total units</FactKey><FactValue>{totalUnits}</FactValue>
            {(occupied + vacant) > 0 && (
              <>
                <FactKey>Occupancy</FactKey>
                <FactValue>{occupied} occupied · {vacant} vacant{(totalUnits - occupied - vacant) > 0 ? ` · ${totalUnits - occupied - vacant} unknown` : ''}</FactValue>
              </>
            )}
            {occupants > 0 && <><FactKey>Expected occupants</FactKey><FactValue>{occupants}</FactValue></>}
            {hasMeaningfulFloors && (
              <>
                <FactKey>Floors</FactKey>
                <FactValue>{floorRange.min === floorRange.max ? `Floor ${floorRange.min}` : `Floors ${floorRange.min}–${floorRange.max}`}</FactValue>
              </>
            )}
          </FactGrid>
        )
      }

      if (rows.length === 0) {
        const SectionIcon = SECTION_ICONS[section.key] || InfoIcon
        return (
          <>
            <FriendlyEmpty>
              <SectionIcon />
              <FriendlyEmptyText>{section.data?.emptyCopy || 'Nothing to show here yet.'}</FriendlyEmptyText>
              {seeAllLink && <SeeAllLink href={seeAllLink}>Open in app →</SeeAllLink>}
            </FriendlyEmpty>
          </>
        )
      }
      const renderRow = (r, idx) => {
        if (section.key === 'forecast') {
          const days = r.daysUntilExpiry ?? 0
          const tone = days <= 7 ? 'overdue' : days <= 30 ? 'expiring_soon' : 'current'
          return (
            <tr key={r.refId || idx}>
              <td>{r.fileTypeName || r.fileTypeCode || '—'}</td>
              <td><StatusChip tone={tone}>{days <= 0 ? 'Due now' : `In ${days}d`}</StatusChip></td>
              <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.expirationDate)}</td>
            </tr>
          )
        }
        if (section.key === 'reports') {
          const status = r.status || '—'
          const tone = status === 'completed' ? 'current' : status === 'failed' ? 'overdue' : null
          return (
            <tr key={r.id || idx}>
              <td>{r.typeName || r.typeCode || 'Report'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{(r.category || '').replace(/_/g, ' ')}</td>
              <td>{tone ? <StatusChip tone={tone}>{status}</StatusChip> : <span style={{ color: '#999', fontSize: 12 }}>{status}</span>}</td>
              <td style={{ color: '#999', fontSize: 12 }}>v{r.version || 1} · {formatDate(r.completedAt || r.createdAt)}</td>
            </tr>
          )
        }
        if (section.key === 'analytics_templates' || section.key === 'checklist_templates') {
          const pct = r.percentage ?? 0
          const barTone = pct === 100 ? '#10b981' : pct >= 70 ? '#33becc' : pct >= 40 ? '#f59e0b' : '#dc2626'
          return (
            <tr key={r.id || idx}>
              <td style={{ minWidth: 200 }}>{r.name || '—'}</td>
              <td style={{ width: 180 }}>
                <div style={{ width: 160, height: 8, background: '#f2f2f2', borderRadius: 9999, overflow: 'hidden' }}>
                  <div style={{ width: `${pct}%`, height: '100%', background: barTone, transition: 'width 0.4s' }} />
                </div>
              </td>
              <td style={{ fontSize: 12, color: '#666', fontWeight: 500, minWidth: 90, textAlign: 'right' }}>
                {pct}% · {r.itemsCovered ?? 0}/{r.totalItems ?? 0}
              </td>
            </tr>
          )
        }
        if (section.key === 'compliance_sets') {
          return (
            <tr key={r.id || idx}>
              <td>{r.name || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{r.framework || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>Weight {r.criticality ?? '—'}</td>
            </tr>
          )
        }
        if (section.key === 'bp_audits') {
          const fallback = `${r.status === 'completed' ? 'Completed audit' : 'Audit'} of ${r.fileCount ?? 0} files`
          return (
            <tr key={r.id || idx}>
              <td>{r.summary || fallback}</td>
              <td><StatusChip tone={r.status === 'completed' ? 'current' : r.status === 'failed' ? 'overdue' : null}>{r.status || '—'}</StatusChip></td>
              <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.completedAt || r.createdAt)}{r.fileCount ? ` · ${r.fileCount} files` : ''}</td>
            </tr>
          )
        }
        if (section.key === 'building_assets') {
          return (
            <tr key={r.id || idx}>
              <td>{r.name || `Asset #${r.id}`}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{r.assetType || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{r.location || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{r.manufacturer || '—'}</td>
            </tr>
          )
        }
        if (section.key === 'units') {
          const floors = Array.isArray(r.floorNumbers) ? r.floorNumbers.join(', ') : null
          const type = r.buildingType
            ? r.buildingType.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase())
            : '—'
          const occLabel = r.occupied === true ? 'Occupied' : r.occupied === false ? 'Vacant' : '—'
          return (
            <tr key={r.id || idx}>
              <td>{r.name || `Unit ${idx + 1}`}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{type}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{floors ?? '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{occLabel}</td>
            </tr>
          )
        }
        if (section.key === 'notes') {
          return (
            <tr key={r.id || idx}>
              <td>{(r.body || '').slice(0, 140) || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.createdAt)}</td>
            </tr>
          )
        }
        if (section.key === 'emails') {
          return (
            <tr key={r.id || idx}>
              <td>{r.subject || '(no subject)'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{r.from || '—'}</td>
              <td style={{ color: '#999', fontSize: 12 }}>{formatDate(r.receivedAt)}</td>
            </tr>
          )
        }
        return (
          <tr key={r.id || idx}>
            <td>{r.title || r.narrative || r.location || '—'}</td>
            <td style={{ color: '#999', fontSize: 12 }}>{r.status || r.severity || '—'}</td>
            <td style={{ color: '#999', fontSize: 12 }}>
              {formatDate(r.deadline || r.occurredAt || r.reportedAt || r.createdAt)}
            </td>
          </tr>
        )
      }
      const headers = section.key === 'units'
        ? ['Unit', 'Type', 'Floor(s)', 'Status']
        : section.key === 'notes'
          ? ['Note', 'Recorded']
          : section.key === 'emails'
            ? ['Subject', 'From', 'Received']
            : section.key === 'forecast'
              ? ['Document type', 'When', 'Expires']
              : section.key === 'reports'
                ? ['Report', 'Category', 'Status', 'Version']
                : (section.key === 'analytics_templates' || section.key === 'checklist_templates')
                  ? ['Template', 'Progress', 'Complete']
                  : section.key === 'compliance_sets'
                    ? ['Set', 'Framework', 'Weight']
                    : section.key === 'bp_audits'
                      ? ['Summary', 'Status', 'When']
                      : section.key === 'building_assets'
                        ? ['Asset', 'Type', 'Location', 'Manufacturer']
                        : ['Title', 'Status', 'When']
      return (
        <>
          {unitsSummary}
          {section.key === 'units' && !hideUnitSampleTable && (
            <div style={{ fontSize: 11, color: '#999', margin: '8px 0 4px' }}>
              Sample units
            </div>
          )}
          {!(section.key === 'units' && hideUnitSampleTable) && (
            <Table>
              <thead>
                <tr>
                  {headers.map((h) => <th key={h}>{h}</th>)}
                </tr>
              </thead>
              <tbody>{rows.map(renderRow)}</tbody>
            </Table>
          )}
          {total > rows.length && !(section.key === 'units' && hideUnitSampleTable) && (
            <div style={{ fontSize: 11, color: '#999', marginTop: 6 }}>
              Showing {rows.length} of {total.toLocaleString()}.
            </div>
          )}
          {seeAllLink && <SeeAllLink href={seeAllLink}>Open in app →</SeeAllLink>}
        </>
      )
    }

    if (section.kind === 'timeline') {
      const events = section.data?.events || []
      return (
        <Timeline>
          {events.map((ev, idx) => (
            <TimelineItem key={idx}>
              <TimelineDot />
              <TimelineWhen>{formatDate(ev.when)}</TimelineWhen>
              <TimelineDesc>{ev.desc}</TimelineDesc>
            </TimelineItem>
          ))}
        </Timeline>
      )
    }

    if (section.kind === 'annotations') {
      const rows = section.data?.rows || []
      if (rows.length === 0) return <Paragraph style={{ color: '#999' }}>No annotations yet.</Paragraph>
      return rows.map((a) => (
        <Paragraph key={a.id} style={{ background: '#fef3c7', borderLeft: '3px solid #f59e0b', padding: '10px 14px', borderRadius: '0 6px 6px 0' }}>
          <strong style={{ fontSize: 10, textTransform: 'uppercase', color: '#92400e' }}>
            Annotation · {a.authorName} · {formatDate(a.createdAt)}
          </strong>
          <div style={{ marginTop: 4 }}>{a.body}</div>
        </Paragraph>
      ))
    }

    return <Paragraph style={{ color: '#999' }}>Section type not yet rendered.</Paragraph>
  }

  return (
    <Section id={`section-${section.key}`}>
      <SectionHead>
        <SectionTitle>{section.title}</SectionTitle>
        {headerActions.length > 0 && (
          <SectionActions>
            {headerActions.map((a, idx) => (
              <WikiActionChip key={idx} Icon={a.Icon} label={a.label} href={a.href} variant={a.variant} title={a.title} />
            ))}
          </SectionActions>
        )}
      </SectionHead>
      {renderBody()}
    </Section>
  )
}

export default WikiSection
